// ============================================================
// ELC Shift — 🎓 Weekly training, the admin tab (v132.1, PR 2 of
// docs/training-plan.md)
//
// Matt, 19 Sep: "shift should suggest weekly training session, but the admin
// can override what they should be with a topic and add some more detail."
// And: approve, never auto-publish.
//
// Three cards: NEXT WEEK (the draft to approve or decline — suggested topic
// and why, Head Office override, the session body, the quiz WITH answers),
// THIS WEEK (the live one), and RECENT. Every decision goes through the
// Worker; this file renders and asks.
//
// The session body arrives ALREADY SANITISED by the Worker (an allow-list of
// tags, attributes dropped) and is rendered with innerHTML inside its own
// box. Everything else on the page goes through escapeHtml.
//
// Free globals consumed: DB, escapeHtml, showToast (admin.html inline core).
// Handlers are EXPORTED (esbuild globalName TrainingAdmin) — never assigned
// onto window as an object (the v125.14 lesson).
// ============================================================

var _state = null;      // the last GET /api/admin/training payload
var _photos = {};       // v132.14 — { sessionId: [{ id, caption, photo }] } once fetched

// v132.14 — the session preview styled like the till shows it (Matt: the
// admin preview "could use the same aside/mark styling"). Same rules as the
// staff tab's ensureStyles, keyed on the tag alone under .tr-body.
var _styled = false;
function ensureStyles() {
  if (_styled || document.getElementById('trainingAdminStyles')) { _styled = true; return; }
  var s = document.createElement('style'); s.id = 'trainingAdminStyles';
  s.textContent = [
    '.tr-body{font-size:15px;line-height:1.65;color:var(--text)}',
    '.tr-body h2{font-size:20px;line-height:1.3;margin:16px 0 8px;color:var(--text)}',
    '.tr-body h3{font-size:16px;margin:16px 0 6px;color:var(--adm-comms-green-text)}',
    '.tr-body p{margin:0 0 10px}',
    '.tr-body ul,.tr-body ol{padding-inline-start:22px;margin:0 0 12px}',
    '.tr-body li{margin:0 0 6px}',
    '.tr-body strong,.tr-body b{color:var(--text);font-weight:800}',
    '.tr-body em,.tr-body i{color:var(--adm-comms-blue-text)}',
    '.tr-body u{text-decoration-color:#e53935;text-decoration-thickness:2px;text-underline-offset:3px;font-weight:700}',
    // v136 dark mode: a highlight stays a yellow highlighter with dark ink in both themes (deliberate).
    '.tr-body mark{background:#fff3b0;color:#111b21;padding:0 4px;border-radius:4px}',
    '.tr-body aside{background:var(--adm-comms-aside-bg);border-inline-start:4px solid #00853e;border-radius:10px;padding:10px 12px;margin:12px 0;font-size:14px}',
    '.tr-body blockquote{margin:10px 0;padding:8px 12px;border-inline-start:4px solid var(--border-strong);background:var(--surface-2);color:var(--adm-comms-ink);font-style:italic;border-radius:0 8px 8px 0}',
  ].join('\n');
  document.head.appendChild(s);
  _styled = true;
}
var _policies = null;   // [{ id, title }] for the override picker
var _busy = false;

function esc(s) { return (typeof escapeHtml === 'function') ? escapeHtml(s == null ? '' : String(s)) : String(s == null ? '' : s); }
function toast(m, kind) { if (typeof showToast === 'function') showToast(m, kind); else console.log('[training] ' + m); }

async function api(path, opts) {
  var o = opts || {};
  var token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  var res = await fetch(base + path, { method: o.method || 'GET', headers: headers, cache: 'no-store', body: o.body ? JSON.stringify(o.body) : undefined });
  var payload = await res.json().catch(function () { return {}; });
  if (!res.ok || payload.ok === false) throw new Error((payload && (payload.error || payload.reason)) || ('HTTP ' + res.status));
  return payload;
}

function root() { return document.getElementById('trainingAdminRoot'); }

function pill(status) {
  // v136 dark mode: status pills are filled with white text, so they keep their colours in both themes.
  var map = { draft: ['#6b7280', 'Draft'], approved: ['#059669', 'Approved'], declined: ['#b91c1c', 'Declined'], published: ['#2563eb', 'Published'], skipped: ['#9ca3af', 'Skipped'], planned: ['#7c3aed', 'Planned'] };   // v132.13: planned
  var m = map[status] || ['#6b7280', status || '?'];
  return '<span style="display:inline-block;background:' + m[0] + ';color:#fff;border-radius:999px;padding:2px 10px;font-size:12px;font-weight:600">' + esc(m[1]) + '</span>';
}

function when(iso) { if (!iso) return ''; try { return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { return String(iso); } }

function policyOptions(selectedIds) {
  var sel = (selectedIds || []).map(Number);
  return '<option value="">Let Shift suggest (from the chat and JACK)</option>' +
    (_policies || []).map(function (p) {
      return '<option translate="no" value="' + esc(p.id) + '"' + (sel.indexOf(Number(p.id)) !== -1 ? ' selected' : '') + '>' + esc(p.title) + '</option>';
    }).join('');
}

// v132.43: WHICH KIND OF SESSION, asked first.
// Matt, 21 Sep: "you see the week. You then choose - 'Your own topic' or
// 'select from polices'. i would rather this was a decision at the
// beginning." It was neither a decision nor at the beginning: the policy
// dropdown was the only visible path and your own material hid inside a
// <details> at the bottom headed "Or train on your own material". You had
// to already know it was there.
//
// null until somebody picks, so the fork is a real question rather than a
// default with an alternative tucked underneath it.
var _topicMode = null;

export function setTopicMode(mode) {
  _topicMode = (mode === 'own' || mode === 'policy') ? mode : null;
  paintTab();
}

function modeButton(mode, icon, label, hint) {
  var on = _topicMode === mode;
  return '<button onclick="TrainingAdmin.setTopicMode(\'' + mode + '\')" style="flex:1 1 220px;text-align:start;font:inherit;cursor:pointer;' +
    'border:2px solid ' + (on ? '#00853e' : 'var(--border-strong)') + ';background:' + (on ? 'var(--adm-comms-ok-soft)' : 'var(--card)') + ';' +
    'border-radius:10px;padding:10px 12px">' +
    '<div style="font-weight:700;font-size:13.5px">' + icon + ' ' + esc(label) + '</div>' +
    '<div style="font-size:12px;color:var(--sub);margin-top:2px">' + esc(hint) + '</div></button>';
}

function overrideForm(session) {
  var s = session || {};
  var head = '<div style="border:1px dashed var(--border-strong);border-radius:10px;padding:12px;margin-top:12px">' +
    '<div style="font-weight:600;margin-bottom:8px">' + (s.id ? 'Regenerate with a different topic' : 'Write next week\'s session') + '</div>' +
    '<div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:10px">' +
      modeButton('policy', '📖', 'Select from policies', 'Written from a policy, and every quiz answer quoted from it.') +
      modeButton('own', '📎', 'Your own topic', 'A new range, a distributor\'s notes, a process that is not in a policy yet.') +
    '</div>';

  if (!_topicMode) {
    return head + '<div style="font-size:12.5px;color:var(--sub)">Pick one to carry on.</div></div>';
  }

  var mid = _topicMode === 'policy'
    ? '<label style="display:block;font-size:13px;color:var(--adm-comms-ink);margin-bottom:6px">Policy to train on' +
        '<select id="trainingPolicyPick" style="display:block;width:100%;margin-top:4px;padding:8px;border:1px solid var(--border-strong);border-radius:8px;font:inherit">' + policyOptions(s.policy_ids) + '</select></label>' +
      '<label style="display:block;font-size:13px;color:var(--adm-comms-ink);margin-bottom:8px">Notes for the writer (optional)' +
        '<textarea id="trainingDetail" maxlength="2000" placeholder="e.g. focus on what to do when a customer has no receipt" style="display:block;width:100%;box-sizing:border-box;min-height:64px;margin-top:4px;padding:8px;border:1px solid var(--border-strong);border-radius:8px;font:inherit">' + esc(s.detail || '') + '</textarea></label>'
    : materialForm(s);

  return head + mid +
    '<button class="btn" onclick="TrainingAdmin.generate()" ' + (_busy ? 'disabled' : '') + '>' + (s.id ? '🔁 Regenerate draft' : '✨ Generate draft') + '</button>' +
    '<span style="font-size:12px;color:var(--sub);margin-inline-start:8px">About a minute. Nothing publishes until it is approved.</span>' +
    '</div>';
}

// v132.24 — Matt: "can they also ask for free form with the option to
// upload? eg a new product range and the distributor gives us training
// materials?" Paste the text, or pick a .txt/.docx (read on this device with
// mammoth from vendor/, never uploaded as a file); the session is written
// from it and every quiz quote is checked against it. PDFs are not read -
// copy the text out of the PDF instead (said on screen). Pictures still go
// in 📷 Pictures after the draft exists.
var _material = '';
function materialForm(s) {
  var have = _material || (s && s.material_text) || '';
  // v132.43: no longer a <details> headed "Or train on your own material".
  // It is one half of a choice the user has already made, so hiding it
  // behind a disclosure triangle would be asking the same question twice.
  return '<div style="margin:8px 0 10px">' +
    '<label style="display:block;font-size:13px;color:var(--adm-comms-ink);margin:8px 0 6px">Session title' +
      '<input id="trainingTopic" maxlength="120" value="' + esc((s && s.source === 'material' && s.topic) || '') + '" placeholder="e.g. The new Bluey range - what to tell customers" style="display:block;width:100%;box-sizing:border-box;margin-top:4px;padding:8px;border:1px solid var(--border-strong);border-radius:8px;font:inherit"></label>' +
    '<label style="display:block;font-size:13px;color:var(--adm-comms-ink);margin-bottom:6px">The material - paste it, or pick a file' +
      '<textarea id="trainingMaterial" maxlength="40000" oninput="TrainingAdmin.materialTyped(this)" placeholder="Paste the product notes / training text here. The session will be written ONLY from this text and every quiz answer checked against it." style="display:block;width:100%;box-sizing:border-box;min-height:110px;margin-top:4px;padding:8px;border:1px solid var(--border-strong);border-radius:8px;font:inherit;font-size:12.5px">' + esc(have) + '</textarea></label>' +
    '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;font-size:12.5px;color:var(--sub)">' +
      '<input type="file" id="trainingMaterialFile" accept=".txt,.docx,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onchange="TrainingAdmin.materialPicked(this)">' +
      '<span id="trainingMaterialState">' + (have ? have.length.toLocaleString() + ' characters' : '.txt or .docx - read on this device, nothing is uploaded. For a PDF, copy the text out of it and paste.') + '</span>' +
      '<label style="margin-inline-start:auto">Week <select id="trainingWeekPick" style="padding:4px 6px;border:1px solid var(--border-strong);border-radius:8px;font:inherit;font-size:12px">' + weekOptions() + '</select></label></div>' +
    '</div>';
}
function weekOptions() {
  var s = _state || {}; var next = s.week && s.week.next; if (!next) return '';
  var out = [];
  for (var i = 0; i < 7; i++) {
    var d = new Date(next + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + 7 * i);
    var ws = d.toISOString().slice(0, 10);
    out.push('<option value="' + ws + '">' + (i === 0 ? 'Next week (' + ws + ')' : 'w/c ' + ws) + '</option>');
  }
  return out.join('');
}
export function materialTyped(ta) {
  _material = ta ? ta.value : '';
  var st = document.getElementById('trainingMaterialState'); if (st) st.textContent = _material ? _material.length.toLocaleString() + ' characters' : '';
}
var _mammothLoading = null;
function loadMammoth() {
  if (typeof window.mammoth !== 'undefined') return Promise.resolve(true);
  if (_mammothLoading) return _mammothLoading;
  _mammothLoading = new Promise(function (resolve) {
    var sc = document.createElement('script'); sc.src = 'vendor/mammoth.browser.min.js';
    var to = setTimeout(function () { resolve(false); }, 10000);
    sc.onload = function () { clearTimeout(to); resolve(typeof window.mammoth !== 'undefined'); };
    sc.onerror = function () { clearTimeout(to); resolve(false); };
    document.head.appendChild(sc);
  });
  return _mammothLoading;
}
export async function materialPicked(input) {
  var f = input && input.files && input.files[0]; if (!f) return;
  var st = document.getElementById('trainingMaterialState'); var ta = document.getElementById('trainingMaterial');
  var say = function (m, bad) { if (st) { st.textContent = m; st.style.color = bad ? 'var(--adm-comms-red-text)' : ''; } };
  say('Reading ' + f.name + '…');
  var text = '';
  try {
    if (/\.docx$/i.test(f.name)) {
      if (!(await loadMammoth())) { say('Could not load the Word reader - paste the text instead.', true); return; }
      var r = await window.mammoth.convertToHtml({ arrayBuffer: await f.arrayBuffer() });
      text = String(r.value || '').replace(/<\/(p|h[1-6]|li|tr|div)>/gi, '\n').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/[ \t]+/g, ' ').replace(/\n\s*\n+/g, '\n\n').trim();
    } else if (/\.pdf$/i.test(f.name)) {
      say('PDFs are not read here - open the PDF, copy the text and paste it above.', true); return;
    } else {
      text = String(await f.text() || '').trim();
    }
  } catch (e) { say('Could not read ' + f.name + ' - paste the text instead.', true); return; }
  if (!text) { say('That file has no readable text - paste it instead.', true); return; }
  if (text.length > 40000) text = text.slice(0, 40000);
  _material = text; if (ta) ta.value = text;
  say(text.length.toLocaleString() + ' characters read from ' + f.name + (text.length >= 40000 ? ' (cut at 40,000)' : ''));
}

// v132.38: how hard is this to read, and which sentences to fix.
// It never blocks approval: a formula is an indicator, and the person
// looking at this screen is a better judge than it is. It is here so the
// judgement is an informed one.
function readabilityBlock(r) {
  if (!r || !r.measured) return '';
  var ok = !(r.problems && r.problems.length);
  var head = 'Reading level: <b>grade ' + esc(String(r.grade)) + '</b> · sentences average <b>' +
    esc(String(r.avg_sentence)) + ' words</b> · longest <b>' + esc(String(r.longest_sentence)) +
    '</b> · long words <b>' + esc(String(r.long_word_pct)) + '%</b>';
  if (ok) {
    return '<div style="color:var(--adm-comms-ok-text);font-size:12.5px;margin:6px 0">📖 ' + head + '. Within target for a second-language reader.</div>';
  }
  var fixes = (r.hard_sentences || []).map(function (h) {
    return '<li style="margin-bottom:3px"><b>' + esc(String(h.words)) + ' words:</b> <span translate="no">' + esc(h.text) + '</span></li>';
  }).join('');
  var words = (r.long_words || []).slice(0, 8).map(function (w) { return esc(w.word); }).join(', ');
  return '<details style="background:var(--adm-comms-warn-soft);border:1px solid var(--adm-comms-warn-line);border-radius:8px;padding:8px 10px;margin:8px 0;font-size:12.5px;color:var(--warn-deep)">' +
    '<summary style="cursor:pointer;font-weight:600">📖 Harder to read than the target' +
      ' <span style="font-weight:400">(' + esc(String((r.problems || []).length)) + ' to look at)</span></summary>' +
    '<div style="margin-top:6px">' + head + '</div>' +
    '<ul style="margin:6px 0;padding-inline-start:18px">' + (r.problems || []).map(function (p) { return '<li>' + esc(p) + '</li>'; }).join('') + '</ul>' +
    (fixes ? '<div style="font-weight:600;margin-top:6px">The longest sentences, worst first:</div><ul style="margin:4px 0;padding-inline-start:18px">' + fixes + '</ul>' : '') +
    (words ? '<div style="margin-top:4px">Long words used most: <span translate="no">' + words + '</span></div>' : '') +
    '<div style="margin-top:6px;font-style:italic">This does not stop you approving it. It is a guide, not a verdict, and it cannot tell whether a word is one your teams know.</div>' +
  '</details>';
}

// v132.39 — the SAME renderer the till uses. A preview that does not look
// like the thing staff see is a preview of nothing, so this calls the shared
// bundle rather than drawing its own version.
function bodyWithExtras(s) {
  if (!s || !s.body_html) return '<span style="color:var(--sub)">No body.</span>';
  if (typeof TrainingBlocks === 'undefined' || !TrainingBlocks.render) return s.body_html;
  return TrainingBlocks.render(s.body_html, s.blocks, s.checkpoints, { rtl: false });
}

// One line saying what the session got, so Head Office can see it without
// reading the whole thing.
function extrasLine(s) {
  if (typeof TrainingBlocks === 'undefined' || !TrainingBlocks.summary) return '';
  var b = (s && s.blocks) || [];
  var c = (s && s.checkpoints) || [];
  var none = !b.length && !c.length;
  return '<div style="font-size:12px;color:' + (none ? 'var(--warn-deep)' : 'var(--sub)') + ';margin:4px 0">'
    + (none ? '\u26A0 ' : '\u{1F5BC} ') + esc(TrainingBlocks.summary(b, c)) + '</div>';
}

function validationBlock(v) {
  if (!v) return '';
  if (v.ok) return '<div style="color:var(--adm-comms-ok-text);font-size:13px;margin:8px 0">✔ Every quiz answer is backed by the policy text.</div>' + readabilityBlock(v.readability);
  return '<div style="background:var(--adm-comms-danger-soft);border:1px solid var(--adm-comms-danger-line);border-radius:8px;padding:10px;margin:8px 0;font-size:13px;color:var(--danger-deep)">' +
    '<div style="font-weight:600;margin-bottom:4px">This draft cannot be approved — the policy check failed:</div>' +
    '<ul style="margin:0;padding-inline-start:18px">' + (v.errors || []).map(function (e) { return '<li>' + esc(e) + '</li>'; }).join('') + '</ul>' +
    '<div style="margin-top:6px">Regenerate it, or pick a different policy.' +
    (v.sessionId ? ' <button class="btn" style="padding:2px 8px;font-size:12px;margin-inline-start:6px" onclick="TrainingAdmin.recheck(' + Number(v.sessionId) + ')" ' + (_busy ? 'disabled' : '') + ' title="Run the policy check again on this draft - no new generation, nothing is spent">🔎 Re-check</button>' : '') +
    '</div></div>' + readabilityBlock(v.readability);
}

function quizBlock(quiz) {
  if (!Array.isArray(quiz) || !quiz.length) return '<div style="color:var(--sub);font-size:13px">No quiz.</div>';
  // translate="no": the quiz is session content (Head Office's or the writer's words), never the catalogue's.
  return '<ol translate="no" style="padding-inline-start:20px;margin:6px 0">' + quiz.map(function (q) {
    var opts = Array.isArray(q.options) ? q.options : [];
    return '<li style="margin:0 0 10px"><div>' + esc(q.q) + '</div>' +
      '<ul style="list-style:none;padding:0;margin:4px 0 0">' + opts.map(function (o, i) {
        var right = i === q.answer_idx;
        return '<li style="font-size:13px;color:' + (right ? 'var(--adm-comms-ok-text)' : 'var(--sub)') + '">' + (right ? '✔ ' : '· ') + esc(o) + '</li>';
      }).join('') + '</ul>' +
      '<div style="font-size:12px;color:var(--sub);margin-top:2px">&ldquo;' + esc(q.policy_quote) + '&rdquo;</div></li>';
  }).join('') + '</ol>';
}

// v132.14 — the "What would you do?" moment, best choice marked, and the pictures.
function scenarioBlock(sc) {
  if (!sc || !sc.situation) return '';
  return '<details style="margin-top:6px"><summary style="cursor:pointer;font-weight:600">🤔 What would you do? (best choice shown)</summary>' +
    '<div translate="no"><div style="font-size:13.5px;margin:6px 0">' + esc(sc.situation) + '</div><ul style="list-style:none;padding:0;margin:0">' +
    (sc.options || []).map(function (o, i) { var best = i === sc.best_idx; return '<li style="font-size:13px;color:' + (best ? 'var(--adm-comms-ok-text)' : 'var(--sub)') + '">' + (best ? '✔ ' : '· ') + esc(o) + '</li>'; }).join('') + '</ul>' +
    '<div style="font-size:12.5px;color:var(--adm-comms-ink);margin-top:4px">' + esc(sc.why) + '</div>' +
    (sc.policy_quote ? '<div style="font-size:12px;color:var(--sub);margin-top:2px">&ldquo;' + esc(sc.policy_quote) + '&rdquo;</div>' : '') + '</div></details>';
}
// v132.25 — one button per language; shows the Arabic body (dir=rtl) once made
function translationBlock(s) {
  if (!s || !s.id || s.status === 'planned') return '';
  var t = s.translations && s.translations.ar;
  var canMake = !!(s.validation && s.validation.ok);
  return '<details style="margin-top:6px" ' + (t ? '' : '') + '><summary style="cursor:pointer;font-weight:600">🌍 Arabic' + (t ? ' <span style="color:var(--adm-comms-ok-text);font-weight:400;font-size:12px">ready - staff see an English / العربية switch</span>' : ' <span style="color:var(--sub);font-weight:400;font-size:12px">not made yet</span>') + '</summary>' +
    '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin:6px 0;font-size:12.5px">' +
      '<button class="btn" onclick="TrainingAdmin.translate(' + Number(s.id) + ', \'ar\')" ' + (canMake && !_busy ? '' : 'disabled') + ' title="One model call (about 30 seconds). A faithful translation of the approved English - the answers stay on the English quiz.">' + (t ? '🔁 Make the Arabic again' : '🌍 Make an Arabic version') + '</button>' +
      '<span style="color:var(--sub)">' + (canMake ? 'Translated from THIS English text - approve the English first, then make the Arabic, so they match.' : 'Available once the draft passes its policy check.') + '</span></div>' +
    (t ? '<div class="tr-body" translate="no" dir="rtl" lang="ar" style="border-left:0;border-right:3px solid var(--border);padding-right:12px;margin:8px 0;text-align:right">' + (t.body_html || '') + '</div>' +
      '<div style="font-size:12px;color:var(--sub)">Quiz and scenario are translated too (' + ((t.quiz || []).length) + ' questions' + (t.scenario ? ', scenario' : '') + ').</div>' : '') +
    '</details>';
}
export function translate(id, lang) {
  return run('Arabic version made', async function () { await api('/api/admin/training/' + id + '/translate', { method: 'POST', body: { lang: lang || 'ar' } }); }, 'Translating the approved English - about 30 seconds. Please do not press again.');
}

function photosBlock(s) {
  if (!s || !s.id || s.status === 'planned') return '';
  var list = _photos[s.id];
  var thumbs = list === undefined ? '<span style="font-size:12px;color:var(--sub)">Loading…</span>'
    : (list.length ? list.map(function (p) { return '<img src="' + (okPhoto(p.photo)) + '" alt="" style="height:80px;max-width:140px;object-fit:cover;border-radius:8px;margin:0 6px 6px 0">'; }).join('') : '<span style="font-size:12px;color:var(--sub)">No pictures yet.</span>');
  return '<details style="margin-top:6px" ' + (list && list.length ? 'open' : '') + '><summary style="cursor:pointer;font-weight:600">📷 Pictures (' + (list ? list.length : '…') + ')</summary>' +
    '<div style="margin:6px 0">' + thumbs + '</div>' +
    '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;font-size:12.5px">' +
      '<input type="file" id="trPhotos' + Number(s.id) + '" accept="image/jpeg,image/png,image/webp" multiple onchange="TrainingAdmin.photosPicked(' + Number(s.id) + ', this)">' +
      (list && list.length ? '<button class="btn" style="padding:3px 9px;font-size:12px" onclick="TrainingAdmin.clearPhotos(' + Number(s.id) + ')">Remove all</button>' : '') +
      '<span style="color:var(--sub)">Up to 3, from your device. They show above the session on every till. Pictures you own or have permission for - not from the web.</span></div></details>';
}
function okPhoto(p) { return /^data:image\/(jpeg|jpg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/.test(String(p || '')) ? String(p) : ''; }

// Canvas compression, the news composer's pattern: max 1280px, stepped JPEG
// quality, hard cap mirroring the Worker's PHOTO_MAX_CHARS.
function compressOne(f) {
  return new Promise(function (resolve) {
    var img = new Image();
    var url = URL.createObjectURL(f);
    img.onload = function () {
      try {
        var scale = Math.min(1, 1280 / Math.max(img.width, img.height));
        var c = document.createElement('canvas');
        c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        var out = null; var qualities = [0.72, 0.55, 0.4];
        for (var i = 0; i < qualities.length; i++) { out = c.toDataURL('image/jpeg', qualities[i]); if (out.length <= 400000) break; }
        resolve(out && out.length <= 400000 ? out : null);
      } catch (e) { resolve(null); } finally { URL.revokeObjectURL(url); }
    };
    img.onerror = function () { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

async function loadPhotosFor(id) {
  try { var r = await api('/api/admin/training/' + id + '/photos'); _photos[id] = r.photos || []; }
  catch (e) { _photos[id] = []; }
}

export async function photosPicked(id, input) {
  var files = input && input.files ? Array.prototype.slice.call(input.files, 0, 3) : [];
  if (!files.length) return;
  var photos = []; var failed = 0;
  for (var i = 0; i < files.length; i++) { var out = await compressOne(files[i]); if (out) photos.push({ photo: out }); else failed++; }
  if (!photos.length) { toast('No usable pictures (JPEG/PNG only, they are compressed on your device)', 'error'); return; }
  if (failed) toast(failed + ' file(s) skipped - pictures only', 'error');
  return run(photos.length + ' picture(s) attached', async function () {
    await api('/api/admin/training/' + id + '/photos', { method: 'POST', body: { photos: photos } });
    delete _photos[id];
  });
}

export function clearPhotos(id) {
  if (!confirm('Remove every picture from this session?')) return;
  return run('Pictures removed', async function () { await api('/api/admin/training/' + id + '/photos', { method: 'POST', body: { photos: [] } }); delete _photos[id]; });
}

function sessionCard(title, s, opts) {
  var o = opts || {};
  if (!s) {
    return '<div class="card" style="padding:14px;margin-bottom:14px"><div style="font-weight:700;margin-bottom:6px">' + esc(title) + '</div>' +
      '<div style="color:var(--sub);font-size:13px">' + esc(o.empty || 'Nothing here yet.') + '</div>' + (o.form ? overrideForm(null) : '') + '</div>';
  }
  var why = (s.suggestion && Array.isArray(s.suggestion.why)) ? s.suggestion.why : [];
  var canDecide = s.status === 'draft' || s.status === 'declined';
  var canApprove = canDecide && s.validation && s.validation.ok;
  // v132.155: an APPROVED session can still be declined until it publishes, so Head Office
  // can swap next week's topic (the server always allowed it; only this button did not;
  // reported 2 Oct 2026: "decline button is shaded/ blocked so unable to do so").
  var canDecline = canDecide || s.status === 'approved';
  return '<div class="card" style="padding:14px;margin-bottom:14px">' +
    '<div style="display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap">' +
      '<div style="font-weight:700">' + esc(title) + ' <span style="color:var(--sub);font-weight:400;font-size:13px">w/c Mon ' + esc(s.week_start) + '</span></div>' + pill(s.status) + '</div>' +
    // v135.2: the topic, the reasons, the notes and the model name are content, so they are translate="no"; the
    // labels around them are separate text, so the Arabic layer can swap them.
    '<div translate="no" style="font-size:17px;font-weight:600;margin:8px 0 2px">' + esc(s.topic) + '</div>' +
    '<div style="font-size:12px;color:var(--sub)"><span>' + (s.source === 'material' ? '📎 Written from Head Office material' + (s.material_text ? ' (' + String(s.material_text).length.toLocaleString() + ' characters)' : '') : s.source === 'override' ? 'Chosen by Head Office' : s.source === 'planned' ? 'Locked in on the timeline' : 'Suggested by Shift') + '</span>' +
      (s.generated_at ? '<span> · drafted ' + esc(when(s.generated_at)) + '</span>' : '') + (s.model ? ' · <span translate="no">' + esc(s.model) + '</span>' : '') + '</div>' +
    (why.length ? '<div style="font-size:13px;color:var(--adm-comms-ink);margin-top:6px"><b>Why this topic:</b> <span translate="no">' + why.map(esc).join(' · ') + '</span></div>' : '') +
    (s.detail ? '<div style="font-size:13px;color:var(--adm-comms-ink);margin-top:4px"><b>Notes:</b> <span translate="no">' + esc(s.detail) + '</span></div>' : '') +
    (s.status === 'declined' && s.decline_reason ? '<div style="font-size:13px;color:var(--danger-deep);margin-top:4px"><b>Declined:</b> <span translate="no">' + esc(s.decline_reason) + '</span></div>' : '') +
    (s.status === 'approved' ? '<div style="font-size:13px;color:var(--adm-comms-ok-text);margin-top:4px">Approved ' + esc(when(s.approved_at)) + ' — publishes Monday 05:05 Dubai.</div>' : '') +
    validationBlock(s.validation ? Object.assign({}, s.validation, { sessionId: s.id }) : s.validation) +
    (o.actions ? '<div style="display:flex;gap:8px;flex-wrap:wrap;margin:10px 0">' +
      '<button class="btn" onclick="TrainingAdmin.approve(' + Number(s.id) + ')" ' + (canApprove && !_busy ? '' : 'disabled') + '>✅ Approve</button>' +
      '<button class="btn" onclick="TrainingAdmin.decline(' + Number(s.id) + ')" ' + (canDecline && !_busy ? '' : 'disabled') + '>✋ Decline</button>' +
      '<button class="btn" onclick="TrainingAdmin.sendApproval(' + Number(s.id) + ')" ' + (canApprove && !_busy ? '' : 'disabled') + ' title="Email the approver group with their personal approve/decline links">📧 Email approvers</button>' +
      '<button class="btn" onclick="TrainingAdmin.preview(' + Number(s.id) + ')" ' + (_busy ? 'disabled' : '') + ' title="The same email, to you only">👀 Preview to me</button>' +
    '</div>' : '') +
    '<details ' + (o.open ? 'open' : '') + ' style="margin-top:8px"><summary style="cursor:pointer;font-weight:600">The session</summary>' +
      '<div class="tr-body" translate="no" style="border-inline-start:3px solid var(--border);padding-inline-start:12px;margin:8px 0">' + bodyWithExtras(s) + '</div></details>' +
    scenarioBlock(s.scenario) +
    translationBlock(s) +
    '<details style="margin-top:6px"><summary style="cursor:pointer;font-weight:600">The quiz (answers shown)</summary>' + quizBlock(s.quiz) + '</details>' +
    photosBlock(s) +
    (o.form ? overrideForm(s) : '') +
    '</div>';
}

function recentList(rows) {
  if (!rows || !rows.length) return '';
  return '<div class="card" style="padding:14px"><div style="font-weight:700;margin-bottom:6px">Recent</div>' +
    '<table style="width:100%;border-collapse:collapse;font-size:13px">' + rows.map(function (r) {
      return '<tr style="border-top:1px solid var(--border)"><td style="padding:6px 4px;white-space:nowrap">w/c ' + esc(r.week_start) + '</td>' +
        '<td translate="no" style="padding:6px 4px">' + esc(r.topic) + '</td><td style="padding:6px 4px">' + pill(r.status) + '</td>' +
        '<td style="padding:6px 4px;color:var(--sub);white-space:nowrap">' + esc(when(r.published_at || r.approved_at || r.declined_at || r.generated_at)) + '</td></tr>';
    }).join('') + '</table></div>';
}

export async function render() {
  _timeline = null;   // v132.43: Refresh means refresh, so the calendar refetches
  _vsOn = null;       // ... and the Virtual Store settings too (a failed load can be retried)
  var el = root(); if (!el) return;
  ensureStyles();   // v132.14
  el.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:8px 2px">Loading training…</div>';
  try {
    if (!_policies) { var p = await api('/api/admin/training/policies'); _policies = p.policies || []; }
    _state = await api('/api/admin/training');
  } catch (e) {
    el.innerHTML = '<span style="color:var(--red);font-size:13px">Could not load training: ' + esc(e.message) + '</span>';
    return;
  }
  var s = _state;
  var header = '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-bottom:12px;font-size:13px">' +
    '<span style="display:inline-block;border-radius:999px;padding:2px 10px;font-weight:600;background:' + (s.enabled ? 'var(--ok-bg);color:var(--ok-deep)' : 'var(--surface-3);color:var(--sub)') + '">' + (s.enabled ? 'ON: publishes approved sessions on Mondays' : 'OFF: nothing publishes to stores yet') + '</span>' +
    '<span style="color:var(--sub)">Approvers: ' + (s.approvers && s.approvers.length ? '<span translate="no">' + esc(s.approvers.join(', ')) + '</span>' : '<b style="color:var(--adm-comms-bad-text)">none set</b>') +
      ' · <a href="#" onclick="switchTab(\'emailhub\');return false">edit in Automated emails</a></span>' +
    '<button class="btn" onclick="TrainingAdmin.runMonday()" ' + (_busy ? 'disabled' : '') + ' title="Publish this week\'s approved session, draft next week\'s, email the approvers — the same step the cron runs">▶ Run the Monday step now</button>' +
    '<button class="btn" onclick="TrainingAdmin.render()" style="margin-inline-start:auto">↻ Refresh</button></div>' +
    // v132.3 — the cron cannot email links without the Worker's public origin.
    (s.act_base_set === false ? '<div style="background:var(--warn-bg);border:1px solid var(--adm-comms-warn-line2);border-radius:8px;padding:8px 10px;margin-bottom:12px;font-size:13px;color:var(--warn-deep)">' +
      '<b>TRAINING_ACT_BASE is not set</b> on this environment — the Monday cron will draft next week\'s session but will NOT send the approval email (the links need the Worker\'s public URL). Set it in Cloudflare; the buttons here work regardless.</div>' : '');
  header += '<div id="vsStrip"></div>';   // v132.101: the Virtual Store strip, above the tabs with the rest of the header
  el.innerHTML = header + tabBar() + '<div id="trainingTabBody"></div>';
  paintTab();
  paintVirtualStore();
}

// ── v132.101 — Virtual Store, the 3D visual merchandising game at /virtual-store/.
// One strip above the tabs: whether stores can see it (Head Office switches;
// an area manager sees the state) and a button to open it here. Opening it
// from here plays as the signed-in manager (?from=admin); their scores sit
// under Head Office or their area, never a store.
var _vsOn = null;
var _vsCap = null;   // v132.103: Ask Jack questions a day, across all stores
var _vsGuest = null;   // v132.108: is the guest link on
// v133.11 - the town's seasonal themes; Head Office can switch any off. Same ids as SEASON_IDS in
// src/worker/virtual-store.js and the game's seasons.js (a test checks all three).
var VS_SEASONS = [['ramadan', 'Ramadan'], ['eid_fitr', 'Eid al-Fitr'], ['eid_adha', 'Eid al-Adha'], ['diwali', 'Diwali'], ['uae_day', 'UAE National Day'], ['qatar_day', 'Qatar National Day'], ['christmas', 'Christmas'], ['new_year', 'New Year'], ['easter', 'Easter'], ['mothers_day', "Mother's Day"], ['valentine', "Valentine's Day"], ['halloween', 'Halloween'], ['back_to_school', 'Back to school'], ['summer', 'Summer']];
var _vsSeasonsOff = [];
var _vsLoadFailed = false;   // the config fetch failed: show that, offer no saves
function isHeadOfficeHere() { return typeof currentAdmin !== 'undefined' && !!currentAdmin && String(currentAdmin.role || '').toLowerCase() === 'admin'; }
async function paintVirtualStore() {
  var box = document.getElementById('vsStrip'); if (!box) return;
  if (_vsOn === null) {
    _vsLoadFailed = false;
    try { var c = await api('/api/virtual-store/config'); _vsOn = !!c.enabled; _vsCap = c.ask_cap != null ? c.ask_cap : null; _vsSeasonsOff = Array.isArray(c.seasons_off) ? c.seasons_off : []; } catch (e) { _vsLoadFailed = true; }
    try { var gs = await api('/api/virtual-store/guest'); _vsGuest = !!gs.enabled; } catch (e) { _vsGuest = false; }
    box = document.getElementById('vsStrip'); if (!box) return;
  }
  // The config did not load: never show a made-up "OFF" with every theme ticked
  // (a Save from that would switch every theme on). Say so, offer no switches or
  // Save buttons, and leave _vsOn null so Refresh fetches again.
  if (_vsLoadFailed) {
    box.innerHTML = '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin:0 0 12px;padding:10px 12px;border:1px solid var(--adm-comms-danger-line);border-radius:10px;background:var(--adm-comms-danger-soft);font-size:13px;color:var(--danger-deep)">' +
      '<b style="font-size:14px">🏬 Virtual Store</b><span>Could not load the Virtual Store settings. Nothing can be changed here until they load.</span>' +
      '<button class="btn" onclick="TrainingAdmin.render()" style="margin-inline-start:auto">↻ Refresh</button></div>';
    return;
  }
  box.innerHTML = '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin:0 0 12px;padding:10px 12px;border:1px solid var(--border);border-radius:10px;background:var(--surface-2);font-size:13px">' +
    '<b style="font-size:14px">🏬 Virtual Store</b>' +
    '<span style="color:var(--sub)">The 3D store game: learn the VM standards, then spot the faults.</span>' +
    '<span style="display:inline-block;border-radius:999px;padding:2px 10px;font-weight:600;background:' + (_vsOn ? 'var(--ok-bg);color:var(--ok-deep)' : 'var(--surface-3);color:var(--sub)') + '">' + (_vsOn ? 'ON: stores see it on their Training screen' : 'OFF: stores cannot see it yet') + '</span>' +
    (isHeadOfficeHere() ? '<button class="btn" onclick="TrainingAdmin.setVirtualStore(' + (_vsOn ? 'false' : 'true') + ')">' + (_vsOn ? 'Switch off' : 'Switch on for stores') + '</button>' : '') +
    (isHeadOfficeHere() && _vsCap != null ? '<label style="display:inline-flex;align-items:center;gap:6px;color:var(--adm-comms-ink)">Ask Jack questions a day, all stores <input id="vsCap" type="number" min="0" max="5000" step="10" value="' + Number(_vsCap) + '" style="width:80px"></label><button class="btn" onclick="TrainingAdmin.setVirtualStoreCap()">Save</button>' : '') +
    '<a class="btn" href="/virtual-store/?from=admin" style="margin-inline-start:auto;text-decoration:none">Open Virtual Store</a></div>' +
    // v132.108 - the guest link for people outside the company (Head Office only)
    (isHeadOfficeHere() ? '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin:-4px 0 12px;padding:10px 12px;border:1px solid var(--border);border-radius:10px;background:var(--surface-2);font-size:13px">' +
      '<b>Guest link</b>' +
      '<span style="display:inline-block;border-radius:999px;padding:2px 10px;font-weight:600;background:' + (_vsGuest ? 'var(--ok-bg);color:var(--ok-deep)' : 'var(--surface-3);color:var(--sub)') + '">' + (_vsGuest ? 'ON' : 'OFF') + '</span>' +
      '<code style="font-size:12px">' + esc(location.origin + '/virtualstore') + '</code>' +
      '<span style="color:var(--sub)">Guests play and hear Jack; scores are not saved; no policy reader or leaderboard.</span>' +
      '<input id="vsGuestPw" type="password" autocomplete="new-password" placeholder="New guest password (8+ characters)" style="min-width:220px">' +
      '<button class="btn" onclick="TrainingAdmin.setGuestPassword()">' + (_vsGuest ? 'Change password' : 'Set password and switch on') + '</button>' +
      (_vsGuest ? '<button class="btn" onclick="TrainingAdmin.guestOff()">Switch guests off</button>' : '') + '</div>' : '') +
    // v133.11 - the town themes through the year (Head Office only): ticked = shown in its dates
    (isHeadOfficeHere() ? '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin:-4px 0 12px;padding:10px 12px;border:1px solid var(--border);border-radius:10px;background:var(--surface-2);font-size:13px">' +
      '<b>Town themes</b><span style="color:var(--sub)">Each one decorates the town in its own dates. Untick to switch one off.</span>' +
      VS_SEASONS.map(function (s) { return '<label style="display:inline-flex;align-items:center;gap:4px"><input type="checkbox" class="vsSeason" id="vsSeason_' + s[0] + '" value="' + s[0] + '"' + (_vsSeasonsOff.indexOf(s[0]) === -1 ? ' checked' : '') + '>' + esc(s[1]) + '</label>'; }).join('') +
      '<button class="btn" onclick="TrainingAdmin.saveSeasons()">Save themes</button></div>' : '');
}
export async function setGuestPassword() {
  var el = document.getElementById('vsGuestPw'); if (!el) return;
  try {
    var r = await api('/api/virtual-store/guest-password', { method: 'POST', body: { password: el.value } });
    el.value = ''; _vsGuest = !!r.enabled;
    toast('Guest password saved. Share it only with the people you invite. Changing it ends every guest pass.', 'success');
  } catch (e) { toast('Could not save: ' + e.message, 'error'); }
  paintVirtualStore();
}
export async function guestOff() {
  try { await api('/api/virtual-store/guest-password', { method: 'POST', body: { enabled: false } }); _vsGuest = false; toast('Guest link switched off. Every guest pass has ended.', 'success'); }
  catch (e) { toast('Could not switch off: ' + e.message, 'error'); }
  paintVirtualStore();
}
export async function saveSeasons() {
  var off = [];
  document.querySelectorAll('input.vsSeason').forEach(function (el) { if (!el.checked) off.push(el.value); });
  try {
    var r = await api('/api/virtual-store/config', { method: 'POST', body: { seasons_off: off } });
    _vsSeasonsOff = Array.isArray(r.seasons_off) ? r.seasons_off : off;
    toast(_vsSeasonsOff.length ? 'Saved. ' + _vsSeasonsOff.length + ' theme' + (_vsSeasonsOff.length === 1 ? ' is' : 's are') + ' switched off.' : 'Saved. Every theme is on.', 'success');
  } catch (e) { toast('Could not save: ' + e.message, 'error'); }
  paintVirtualStore();
}
export async function setVirtualStoreCap() {
  var el = document.getElementById('vsCap'); if (!el) return;
  try {
    var r = await api('/api/virtual-store/config', { method: 'POST', body: { ask_cap: Number(el.value) } });
    _vsCap = r.ask_cap;
    toast('Saved. Jack answers up to ' + _vsCap + ' questions a day across all stores.', 'success');
  } catch (e) { toast('Could not save: ' + e.message, 'error'); }
  paintVirtualStore();
}
export async function setVirtualStore(on) {
  try {
    var r = await api('/api/virtual-store/config', { method: 'POST', body: { enabled: !!on } });
    _vsOn = !!r.enabled;
    toast(_vsOn ? 'Virtual Store is on. Stores see it on their Training screen.' : 'Virtual Store is off for stores.', 'success');
  } catch (e) { toast('Could not change Virtual Store: ' + e.message, 'error'); }
  paintVirtualStore();
}

// ── v132.23 — three tabs (Matt, 20 Sep: "can this week, future weeks and
// compliance be different tabs?"). THIS WEEK = the live session, read it as
// the till shows it. FUTURE WEEKS = next week's draft to approve/decline/
// regenerate, then the timeline (lock in, view any week). COMPLIANCE = who
// has passed, how it went. The chosen tab survives a refresh (localStorage).
var _tab = 'week';
try { _tab = localStorage.getItem('trainingAdminTab') || 'week'; } catch (e) { _tab = 'week'; }   // private mode: no storage
var TABS = [['week', '📖 This week'], ['future', '🗓 Future weeks'], ['compliance', '📊 Compliance']];
function tabBar() {
  var s = _state || {};
  var attention = { future: !!(s.next && (s.next.status === 'draft' || s.next.status === 'declined')) };
  return '<div id="trainingTabBar" style="display:flex;gap:6px;margin:0 0 14px;border-bottom:2px solid var(--border)">' + TABS.map(function (t) {
    var on = t[0] === _tab;
    return '<button type="button" onclick="TrainingAdmin.showTab(\'' + t[0] + '\')" style="border:0;background:none;padding:8px 12px;margin-bottom:-2px;font:inherit;font-size:14px;cursor:pointer;color:' + (on ? 'var(--adm-comms-green-text)' : 'var(--adm-comms-ink)') + ';font-weight:' + (on ? '800' : '600') + ';border-bottom:2px solid ' + (on ? '#00853e' : 'transparent') + '">' +
      t[1] + (attention[t[0]] ? ' <span title="A draft is waiting for a decision" style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#f97316;vertical-align:middle;margin-inline-start:2px"></span>' : '') + '</button>';
  }).join('') + '</div>';
}
export function showTab(t) {
  if (!TABS.some(function (x) { return x[0] === t; })) return;
  _tab = t;
  try { localStorage.setItem('trainingAdminTab', t); } catch (e) { t = t; }   // private mode: not remembered
  paintTab();
}
function paintTab() {
  var s = _state; var body = document.getElementById('trainingTabBody'); if (!body || !s) return;
  var bar = document.getElementById('trainingTabBar');
  if (bar) { var tmp = document.createElement('div'); tmp.innerHTML = tabBar(); bar.replaceWith(tmp.firstChild); }
  if (_tab === 'week') {
    body.innerHTML = sessionCard('This week', s.current, { actions: false, form: false, open: true, empty: 'Nothing published for the week of Monday ' + s.week.current + '. Approved sessions publish on Monday at 05:05 Dubai - see Future weeks.' }) +
      (s.current ? '<div style="font-size:13px;color:var(--sub);margin:-4px 0 12px">Who has passed it: <a href="#" onclick="TrainingAdmin.showTab(\'compliance\');return false">Compliance</a></div>' : '');
    loadPhotosInto([s.current]);
  } else if (_tab === 'future') {
    // v132.43: two views, which is what Matt asked for: the CALENDAR to see
    // the direction of travel, and the PREVIEW to read and approve what is
    // actually written. Before this they were stacked on one long page and
    // the calendar was sixteen one-line rows at the bottom of it.
    if (_futureView === 'calendar') {
      body.innerHTML = futureViewBar() +
        '<div class="card" style="padding:14px">' + calendarGrid() + '</div>' +
        weekDecision();
      loadTimeline();
    } else {
      body.innerHTML = futureViewBar() +
        sessionCard('Next week', s.next, { actions: true, form: true, open: true, empty: 'No draft for the week of Monday ' + s.week.next + ' yet. Shift will draft one on Monday at 05:30 Dubai - or generate it now.' }) +
        '<div id="trainingViewSlot">' + (_viewed ? sessionCard('w/c ' + _viewed.week_start, _viewed, { actions: true, form: false, open: true }) : '') + '</div>';
      loadTimeline();
      if (_viewed) view(_viewed.id, true);   // v132.18 — keep the opened week open across a re-render (v133.86: quietly)
      loadPhotosInto([s.next]);
    }
  } else {
    // v132.44: the period bar sits above whichever view is chosen. One week
    // is still the default and still what the rest of the app links to.
    if (_period) {
      body.innerHTML = periodBar() + periodPanel();
      if (!_periodData) loadPeriod();
    } else {
      body.innerHTML = periodBar() + compliancePanel();
      loadCompliance(_complianceWeek || s.week.current);
    }
  }
}
// v132.14 — the pictures for a card, then re-render its photo block in place
function loadPhotosInto(list) {
  list.forEach(function (sess) {
    if (!sess || !sess.id) return;
    var done = function () {
      var inp = document.getElementById('trPhotos' + sess.id);
      var det = inp ? inp.closest('details') : null;
      if (det) { var tmp = document.createElement('div'); tmp.innerHTML = photosBlock(sess); det.replaceWith(tmp.firstChild); }
    };
    if (_photos[sess.id] !== undefined) { done(); return; }
    loadPhotosFor(sess.id).then(done);
  });
}

// ── v132.13: the timeline ────────────────────────────────────────────────────
// Matt: "can we see a list of future suggested training and past training?
// Almost like a training timeline." Past weeks (how they went) -> this week
// -> next week -> the next six as SUGGESTIONS Head Office can lock in early.
// A locked topic is what the Monday step drafts from. The "Recent" table it
// replaces is inside it now.
var _timeline = null;



// ── The calendar (v132.43) ────────────────────────────────────────────────
//
// Matt, 21 Sep: "Calendar view i would like bigger so we can understand the
// direction on L&D", and "Maybe sub tabs to show calendar and a preview of
// future weeks?"
//
// It was sixteen one-line rows in a single card, each line a date, a topic
// and a control. You could read any one week off it and you could not see
// the SHAPE of the next two months, which is the only reason to look at a
// calendar at all.
//
// Now it is a grid of week cards. Past weeks carry how they went, the
// current and next weeks stand out, and the six ahead show what is locked
// in against what is only suggested. Somebody glancing at it should be able
// to say "we have three weeks of nothing in November" without reading a
// word, which is the actual question being asked of an L&D plan.

var _futureView = 'calendar';
var _pickedWeek = null;   // the future week the decision panel is about

export function setFutureView(v) {
  _futureView = (v === 'preview') ? 'preview' : 'calendar';
  paintTab();
}

export function pickFutureWeek(weekStart, i) {
  _pickedWeek = (_pickedWeek && _pickedWeek.week_start === weekStart) ? null : { week_start: weekStart, i: Number(i) };
  paintTab();
}

function futureViewBar() {
  var b = function (v, label) {
    var on = _futureView === v;
    return '<button onclick="TrainingAdmin.setFutureView(\'' + v + '\')" style="font:inherit;font-size:12.5px;font-weight:800;padding:5px 12px;cursor:pointer;border:1px solid var(--border-strong);' +
      (on ? 'background:#00853e;color:#fff;border-color:#00853e' : 'background:var(--card);color:var(--adm-comms-ink)') + '">' + label + '</button>';
  };
  return '<div style="display:inline-flex;border-radius:9px;overflow:hidden;margin-bottom:12px">' +
    b('calendar', '\u{1F4C5} Calendar') + b('preview', '\u{1F4C4} Preview') + '</div>';
}

// One card. Colour carries the state so the grid reads before the words do.
function weekCard(o) {
  var bg = o.tone === 'now' ? 'var(--adm-comms-ok-soft)' : (o.tone === 'gap' ? 'var(--adm-comms-warn-soft)' : 'var(--card)');
  var edge = o.tone === 'now' ? '#00853e' : (o.tone === 'locked' ? '#7c3aed' : (o.tone === 'gap' ? 'var(--adm-comms-warn-line)' : (o.tone === 'past' ? 'var(--adm-comms-past-line)' : 'var(--border)')));
  var sel = o.selected;
  return '<div' + (o.onclick ? ' onclick="' + o.onclick + '" style="cursor:pointer;' : ' style="') +
    'border:' + (sel ? '2px solid #00853e' : '1px solid ' + edge) + ';border-inline-start:5px solid ' + edge + ';' +
    'background:' + bg + ';border-radius:10px;padding:9px 11px;min-height:86px;display:flex;flex-direction:column;gap:2px">' +
    '<div style="font-size:11px;color:var(--sub);font-weight:700;letter-spacing:.3px">' + esc(o.when) + '</div>' +
    '<div style="font-size:13px;line-height:1.3;font-weight:' + (o.strong ? '700' : '400') + ';color:' + (o.muted ? 'var(--adm-comms-faint)' : 'var(--text)') + '">' + o.topic + '</div>' +
    (o.foot ? '<div style="font-size:11.5px;color:var(--sub);margin-top:auto">' + o.foot + '</div>' : '') +
  '</div>';
}

function calendarGrid() {
  var t = _timeline;
  if (!t) return '<div style="font-size:13px;color:var(--sub)">Loading the calendar...</div>';
  if (t.error) return '<div style="font-size:13px;color:var(--adm-comms-bad-text)">' + esc(t.error) + '</div>';
  var cards = [];

  (t.past || []).slice().reverse().forEach(function (r) {
    var how = r.status === 'published' && r.pct != null
      ? '<b style="color:' + (r.pct >= 90 ? 'var(--adm-comms-ok-text)' : r.pct >= 60 ? 'var(--adm-comms-amber-text)' : 'var(--adm-comms-bad-text)') + '">' + r.pct + '%</b> passed'
      : esc(r.status);
    cards.push(weekCard({ when: 'w/c ' + r.week_start, topic: '<span translate="no">' + esc(r.topic || '-') + '</span>', foot: how, tone: 'past' }));
  });

  var cur = t.current;
  cards.push(weekCard({ when: 'THIS WEEK', strong: true, tone: 'now',
    topic: cur ? '<span translate="no">' + esc(cur.topic) + '</span>' : '<span style="color:var(--adm-comms-faint)">Nothing published</span>',
    foot: cur ? 'Live now' : 'Nothing went out' }));

  var nx = t.next;
  cards.push(weekCard({ when: 'NEXT WEEK', strong: true, tone: 'now',
    topic: nx ? '<span translate="no">' + esc(nx.topic) + '</span>' : '<span style="color:var(--adm-comms-faint)">No draft yet</span>',
    foot: nx ? esc(nx.status) : 'The Monday step will draft one',
    onclick: "TrainingAdmin.setFutureView('preview')" }));

  (t.future || []).forEach(function (f, i) {
    var sess = f.session;
    if (sess) {
      cards.push(weekCard({ when: 'w/c ' + f.week_start, tone: 'locked', strong: true,
        topic: '\u{1F512} <span translate="no">' + esc(sess.topic) + '</span>',
        foot: sess.status === 'planned'
          ? '<a href="#" onclick="TrainingAdmin.unplan(' + Number(sess.id) + ');return false">Unlock</a>'
          : '<a href="#" onclick="TrainingAdmin.view(' + Number(sess.id) + ');TrainingAdmin.setFutureView(\'preview\');return false">View</a> \u00b7 ' + esc(sess.status) }));
      return;
    }
    var sg = f.suggestion;
    var picked = !!(_pickedWeek && _pickedWeek.week_start === f.week_start);
    cards.push(weekCard({ when: 'w/c ' + f.week_start, tone: 'gap', muted: !sg, selected: picked,
      topic: sg ? '<span translate="no">' + esc(sg.topic) + '</span>' : 'Nothing planned',
      foot: sg ? 'Suggested \u00b7 tap to choose' : 'Tap to choose',
      onclick: "TrainingAdmin.pickFutureWeek('" + esc(f.week_start) + "'," + i + ')' }));
  });

  var unplanned = (t.future || []).filter(function (f) { return !f.session; }).length;
  var head = '<div style="font-size:12.5px;color:var(--sub);margin-bottom:8px">' +
    'Eight weeks behind, then the next six. ' +
    (unplanned ? '<b style="color:var(--warn-deep)">' + unplanned + ' of the next six weeks has nothing locked in.</b> Tap a week to choose its topic.' : 'Every one of the next six weeks has a topic locked in.') +
  '</div>';
  return head + '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:10px">' + cards.join('') + '</div>';
}

// The panel under the grid: the week you tapped, and the same two-way
// choice the next-week form asks. Locking a policy in is one tap; writing
// the whole session now is the paid one, so it says so.
function weekDecision() {
  if (!_pickedWeek) return '';
  var i = _pickedWeek.i;
  var ws = _pickedWeek.week_start;
  var f = ((_timeline && _timeline.future) || [])[i];
  var sg = f && f.suggestion;
  var sel = '<select id="tlPick' + i + '" style="min-width:240px;padding:6px 8px;border:1px solid var(--border-strong);border-radius:8px;font:inherit;font-size:13px">' +
    (_policies || []).map(function (p) {
      return '<option translate="no" value="' + esc(p.id) + '"' + (sg && sg.policy_ids && Number(sg.policy_ids[0]) === Number(p.id) ? ' selected' : '') + '>' + esc(p.title) + '</option>';
    }).join('') + '</select>';
  return '<div style="border:2px solid #00853e;border-radius:12px;padding:14px;margin-top:14px;background:var(--adm-comms-ok-soft)">' +
    '<div style="font-weight:700;margin-bottom:2px">Week commencing ' + esc(ws) + '</div>' +
    (sg && sg.why && sg.why.length ? '<div style="font-size:12px;color:var(--sub);margin-bottom:8px">Suggested because: <span translate="no">' + esc(sg.why[sg.why.length - 1]) + '</span></div>' : '<div style="height:6px"></div>') +
    '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">' + sel +
      '<button class="btn" onclick="TrainingAdmin.plan(\'' + esc(ws) + '\',' + i + ')" ' + (_busy ? 'disabled' : '') + ' title="Just set the topic. The Monday step writes it nearer the time.">\u{1F512} Lock the topic in</button>' +
      '<button class="btn" onclick="TrainingAdmin.draftWeek(\'' + esc(ws) + '\',' + i + ')" ' + (_busy ? 'disabled' : '') + ' title="Write the whole session now, from the policy chosen here. About a minute.">\u270D\uFE0F Write it now</button>' +
      '<button class="btn" onclick="TrainingAdmin.pickFutureWeek(\'' + esc(ws) + '\',' + i + ')">Cancel</button>' +
    '</div>' +
    '<div style="font-size:11.5px;color:var(--sub);margin-top:8px">Locking the topic in costs nothing. Writing it now spends one paid call.</div>' +
  '</div>';
}

// v132.18 — the timeline's View: fetch one session (any week) and show the full card
var _viewed = null;
// v133.86 (overnight sweep, L): only the LATEST request is shown (a repaint re-asks for the week already
// open, and if that answer landed after a newer "View" the old week came back); its pictures load (they sat
// on "Loading..." for ever); and only a person's View scrolls to it, never a repaint.
// The week the PERSON last asked for; a repaint (quiet) re-asks for that one, never an older week.
var _wantViewId = null;
export async function view(id, quiet) {
  if (!quiet || _wantViewId == null) _wantViewId = id;
  var want = _wantViewId, r;
  try { r = await api('/api/admin/training/' + want); }
  catch (e) { if (want === _wantViewId && !quiet) toast('Could not load that week: ' + e.message, 'error'); return; }
  if (want !== _wantViewId) return;   // a newer View has been asked for since
  _viewed = r.session || null;
  var slot = document.getElementById('trainingViewSlot');
  if (slot && _viewed) {
    slot.innerHTML = sessionCard('w/c ' + _viewed.week_start, _viewed, { actions: true, form: false, open: true });
    loadPhotosInto([_viewed]);
    if (!quiet) slot.scrollIntoView({ block: 'start' });
  }
}

export function recheck(id) {
  return run('Re-checked', async function () {
    var r = await api('/api/admin/training/' + id + '/revalidate', { method: 'POST', body: {} });
    if (r.validation && !r.validation.ok) toast('Still failing: ' + (r.validation.errors[0] || ''), 'error');
    if (_viewed && _viewed.id === id) _viewed = r.session || _viewed;
  });
}

// v132.43: the calendar IS the tab now, so this repaints the TAB when the
// fetch lands. It used to hunt for the card whose text began "Timeline" and
// swap that one card; that card no longer exists, and without the change
// the calendar would have sat on "Loading the calendar..." for ever after
// its own fetch came back.
//
// Repainting the tab means paintTab asks for the timeline again, so without
// a guard this is a network loop: paint, fetch, paint, fetch. _timelineAt
// is that guard. Passing force:true is the Refresh button saying it means
// it, which is the only way back in once the data is loaded.
var _timelineAt = 0;
var _timelineBusy = false;
var TIMELINE_RETRY_MS = 30000;

async function loadTimeline(force) {
  if (_timelineBusy) return;
  // v133.83 (sweep J): a failed load used to retry on every repaint, and every load repaints: a loop of
  // fetches for as long as the tab was open. A failure now waits TIMELINE_RETRY_MS before trying again
  // (or the Refresh button, force).
  if (!force && _timeline && (!_timeline.error || Date.now() - _timelineAt < TIMELINE_RETRY_MS)) return;
  _timelineBusy = true;
  try { _timeline = await api('/api/admin/training/timeline'); }
  catch (e) { _timeline = { error: e.message }; }
  _timelineBusy = false;
  _timelineAt = Date.now();
  var el = root(); if (!el || !_state) return;
  if (_tab === 'future') paintTab();
}

export function plan(weekStart, i) {
  var pick = document.getElementById('tlPick' + i);
  var policyId = pick ? Number(pick.value) : null;
  if (!policyId) { toast('Pick a policy first', 'error'); return; }
  var note = prompt('Any note for the writer? (optional)');
  if (note === null) return;
  return run('Locked in', async function () { await api('/api/admin/training/plan', { method: 'POST', body: { week_start: weekStart, policy_ids: [policyId], detail: note || '' } }); });
}

// v132.26 — write the full session for a future week from its suggested (or
// chosen) policy, so it can be read and approved weeks ahead. Uses the same
// generate call as next week (same guard, same cap); the week then shows as
// a draft row with 👁 View.
export function draftWeek(weekStart, i) {
  var pick = document.getElementById('tlPick' + i);
  var policyId = pick ? Number(pick.value) : null;
  if (!policyId) { toast('Pick a policy first', 'error'); return; }
  if (!confirm('Write the full session for the week of ' + weekStart + ' now?\n\nOne model call, about a minute. You can read, approve, decline or regenerate it afterwards from the timeline (View).')) return;
  return run('Draft written', async function () {
    await api('/api/admin/training/generate', { method: 'POST', body: { week_start: weekStart, policy_ids: [policyId] } });
  }, 'Writing the session for w/c ' + weekStart + ' - about a minute. Please do not press again.');
}

export function unplan(id) {
  if (!confirm('Unlock this week? Shift will suggest a topic again.')) return;
  return run('Unlocked', async function () { await api('/api/admin/training/' + id + '/unplan', { method: 'POST', body: {} }); });
}

// ── v132.3: the compliance report ───────────────────────────────────────────
var _compliance = null;        // { week_start, report, weeks } for the selected week
var _complianceWeek = null;


// ── Compliance over a period (v132.44) ───────────────────────────────────
//
// Matt: "On compliance i would like a wtd view, previous weeks, month to
// date, previous months and a YTD and previous years view."
//
// The tab answered one question: how did THIS week go. Right on a Monday,
// wrong in a review, where what matters is whether a store has kept it up.
// Six amber weeks and one green week look identical if you only ever open
// the green one.
//
// null = the single-week view, which is still the default and still what
// everything else in the app links to.
var _period = null;
var _periodData = null;

var PERIODS = [
  ['wtd', 'This week'], ['last_week', 'Last week'],
  ['mtd', 'Month to date'], ['last_month', 'Last month'],
  ['ytd', 'Year to date'], ['last_year', 'Last year'],
];

export function setPeriod(key) {
  _period = key || null;
  _periodData = null;
  paintTab();
  if (_period) loadPeriod();
}

async function loadPeriod() {
  try {
    _periodData = await api('/api/admin/training/compliance?period=' + encodeURIComponent(_period));
  } catch (e) { _periodData = { error: e.message }; }
  if (_tab === 'compliance') paintTab();
}

function periodBar() {
  var b = function (key, label) {
    var on = _period === key;
    return '<button onclick="TrainingAdmin.setPeriod(\'' + key + '\')" style="font:inherit;font-size:12px;font-weight:700;padding:4px 10px;margin:0 4px 4px 0;cursor:pointer;border-radius:8px;border:1px solid ' +
      (on ? '#00853e' : 'var(--border-strong)') + ';background:' + (on ? '#00853e' : 'var(--card)') + ';color:' + (on ? '#fff' : 'var(--adm-comms-ink)') + '">' + label + '</button>';
  };
  var one = '<button onclick="TrainingAdmin.setPeriod(\'\')" style="font:inherit;font-size:12px;font-weight:700;padding:4px 10px;margin:0 8px 4px 0;cursor:pointer;border-radius:8px;border:1px solid ' +
    (_period ? 'var(--border-strong)' : '#00853e') + ';background:' + (_period ? 'var(--card)' : '#00853e') + ';color:' + (_period ? 'var(--adm-comms-ink)' : '#fff') + '">One week</button>';
  return '<div style="margin-bottom:10px">' + one +
    PERIODS.map(function (p) { return b(p[0], p[1]); }).join('') + '</div>';
}

function periodPanel() {
  var d = _periodData;
  if (!d) return '<div style="font-size:13px;color:var(--sub)">Working it out...</div>';
  if (d.error) return '<div style="font-size:13px;color:var(--adm-comms-bad-text)">' + esc(d.error) + '</div>';
  var r = d.report || {};
  if (!r.sessions) {
    return '<div style="background:var(--surface-2);border:1px solid var(--border);border-radius:10px;padding:14px;font-size:13.5px">' +
      esc(r.headline || 'No sessions in this period.') +
      '<div style="font-size:12px;color:var(--sub);margin-top:6px">Nothing was set, so nothing was missed. This is not nought per cent.</div></div>';
  }
  var rows = (r.stores || []).map(function (x) {
    var col = x.pct == null ? 'var(--sub)' : (x.pct >= 90 ? 'var(--adm-comms-ok-text)' : x.pct >= 75 ? 'var(--adm-comms-amber-text)' : 'var(--adm-comms-bad-text)');
    return '<tr>' +
      '<td style="padding:4px 10px 4px 0;font-size:13px">' + esc(x.store) + '</td>' +
      '<td style="padding:4px 10px 4px 0;text-align:end;font-weight:700;color:' + col + '">' + (x.pct == null ? '-' : x.pct + '%') + '</td>' +
      '<td style="padding:4px 10px 4px 0;text-align:end;font-size:12px;color:var(--sub)">' + x.passed + ' of ' + x.expected + '</td>' +
      '<td style="padding:4px 0;font-size:12px;color:var(--sub)">' + (x.worst ? 'worst week ' + esc(x.worst.week_start) + ' at ' + x.worst.pct + '%' : '') + '</td>' +
    '</tr>';
  }).join('');
  return '<div style="background:var(--card);border:1px solid var(--border);border-radius:10px;padding:14px">' +
    '<div style="font-size:14px;font-weight:700;margin-bottom:2px">' + esc(r.headline || '') + '</div>' +
    '<div style="font-size:12px;color:var(--sub);margin-bottom:10px">' + esc(r.range ? r.range.from + ' to ' + r.range.to : '') +
      ' \u00b7 ' + r.sessions + ' session(s) \u00b7 counted against active staff, and it does NOT take leave off, so a person away all month still counts. The single-week view does.</div>' +
    '<table style="border-collapse:collapse;width:100%"><thead><tr>' +
      '<th style="text-align:start;font-size:11px;text-transform:uppercase;color:var(--sub);padding-bottom:4px">Store</th>' +
      '<th style="text-align:end;font-size:11px;text-transform:uppercase;color:var(--sub)">Passed</th>' +
      '<th style="text-align:end;font-size:11px;text-transform:uppercase;color:var(--sub)">Of</th>' +
      '<th style="text-align:start;font-size:11px;text-transform:uppercase;color:var(--sub);padding-inline-start:10px">Worst week</th>' +
    '</tr></thead><tbody>' + rows + '</tbody></table></div>';
}
function compliancePanel() {
  var c = _compliance;
  var weeks = (c && c.weeks) || [];
  var opts = weeks.map(function (w) {
    return '<option translate="no" value="' + esc(w.week_start) + '"' + (c && c.week_start === w.week_start ? ' selected' : '') + '>w/c ' + esc(w.week_start) + ' — ' + esc(w.topic) + ' (' + w.pct + '%)</option>';
  }).join('');
  // v135.2: data-kind marks this card so loadCompliance can find it without reading its (translatable) heading.
  var head = '<div class="card" data-kind="compliance" style="padding:14px;margin-bottom:14px"><div style="display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap">' +
    '<div style="font-weight:700">Compliance</div>' +
    (weeks.length ? '<select onchange="TrainingAdmin.pickWeek(this.value)" style="padding:6px 8px;border:1px solid var(--border-strong);border-radius:8px;font:inherit">' + opts + '</select>' : '') + '</div>';
  if (!c) return head + '<div style="font-size:13px;color:var(--sub);margin-top:6px">Loading…</div></div>';
  if (!c.report) return head + '<div style="font-size:13px;color:var(--sub);margin-top:6px">Nothing published for the week of ' + esc(c.week_start) + '.</div></div>';
  var r = c.report;
  // v135.2: each figure is its own span, so the Arabic layer can match it (one long text node never matched).
  var summary = '<div style="font-size:13px;color:var(--adm-comms-ink);margin:6px 0 10px"><b translate="no">' + esc(r.session.topic) + '</b> · <span>' + r.confirmed + ' of ' + r.total + ' passed (' + (r.total ? Math.round((r.confirmed / r.total) * 100) : 0) + '%)</span>' +
    (r.excluded ? ' · <span>' + r.excluded + ' excluded (on leave, transferred in later, or left)</span>' : '') +
    (r.attempted_not_passed ? ' · <span>' + r.attempted_not_passed + ' tried but not yet passed</span>' : '') + '</div>';
  var howItWent = scoresBlock(r.scores);   // v132.12
  var rows = (r.stores || []).map(function (st) {
    var colour = st.pct >= 90 ? 'var(--adm-comms-ok-text)' : st.pct >= 60 ? 'var(--adm-comms-amber-text)' : 'var(--adm-comms-bad-text)';
    return '<details style="border-top:1px solid var(--border);padding:6px 2px"><summary style="cursor:pointer;display:flex;justify-content:space-between;gap:8px;list-style:none">' +
      '<span>' + esc(st.store) + '</span><span><b style="color:' + colour + '">' + st.pct + '%</b> <span style="color:var(--sub)">' + st.confirmed + '/' + st.total + '</span></span></summary>' +
      (st.names && st.names.missing && st.names.missing.length
        ? '<div style="font-size:12.5px;color:var(--danger-deep);padding:4px 0 2px 12px">Not yet: <span translate="no">' + st.names.missing.map(esc).join(', ') + '</span></div>'
        : '<div style="font-size:12.5px;color:var(--adm-comms-ok-text);padding:4px 0 2px 12px">Everyone has passed.</div>') + '</details>';
  }).join('');
  return head + summary + howItWent + (rows || '<div style="font-size:13px;color:var(--sub)">No stores in scope.</div>') + '</div>';
}

// v132.12 — "How do we see if the training has gone well?" Four numbers OF
// THOSE WHO TRIED (compliance above is of everyone), then the question most
// people got wrong — the policy point the team does not understand — from
// the anonymous per-question counter. Nobody is named here, ever.
function scoresBlock(sc) {
  if (!sc || !sc.attempted) return '<div style="font-size:12.5px;color:var(--sub);margin:0 0 10px">How it went: no attempts yet.</div>';
  var tile = function (n, l) {
    return '<div style="flex:1;min-width:104px;background:var(--surface-2);border:1px solid var(--border);border-radius:10px;padding:8px 10px">' +
      '<div style="font-size:20px;font-weight:800;line-height:1.1">' + n + '</div><div style="font-size:11.5px;color:var(--sub);margin-top:2px">' + l + '</div></div>';
  };
  var pct = function (v) { return v == null ? '–' : v + '%'; };
  var tiles = '<div style="display:flex;gap:8px;flex-wrap:wrap;margin:4px 0 8px">' +
    tile(pct(sc.pass_rate), 'passed, of ' + sc.attempted + ' who tried') +
    tile(pct(sc.first_attempt_pass_rate), 'passed first time') +
    tile(sc.avg_score == null ? '–' : sc.avg_score + '<span style="font-size:13px;color:var(--sub)">/' + sc.total_questions + '</span>', 'average score') +
    tile(sc.avg_attempts == null ? '–' : sc.avg_attempts, 'attempts each') + '</div>';
  var h = sc.hardest;
  var hardest = h ? '<div style="background:var(--adm-comms-orange-bg);border:1px solid var(--adm-comms-orange-line);border-radius:8px;padding:8px 10px;margin:0 0 8px;font-size:13px;color:var(--adm-comms-orange-text)">' +
    '<b>Most got wrong:</b> Q' + (h.idx + 1) + ' &ldquo;<span translate="no">' + esc(h.q) + '</span>&rdquo; &mdash; only <b>' + pct(h.pct) + '</b> answered it right (' + h.correct + ' of ' + h.asked + '). That is the point to go over.</div>' : '';
  var bars = (sc.questions || []).map(function (q) {
    var p = q.pct == null ? 0 : q.pct;
    var colour = q.pct == null ? 'var(--border)' : (p >= 80 ? '#059669' : p >= 50 ? '#d97706' : '#b91c1c');
    return '<div style="display:flex;align-items:center;gap:8px;font-size:12px;margin:3px 0"><span style="width:26px;color:var(--sub)">Q' + (q.idx + 1) + '</span>' +
      '<span style="flex:1;background:var(--surface-3);border-radius:999px;height:8px;overflow:hidden"><span style="display:block;height:8px;width:' + p + '%;background:' + colour + '"></span></span>' +
      '<span style="width:88px;text-align:end;color:var(--adm-comms-ink)">' + pct(q.pct) + ' <span style="color:var(--adm-comms-faint)">(' + q.asked + ')</span></span></div>';
  }).join('');
  return '<div style="margin:0 0 12px"><div style="font-weight:600;font-size:13px;margin-bottom:2px">How it went</div>' + tiles + hardest +
    (bars ? '<details><summary style="cursor:pointer;font-size:12.5px;color:var(--adm-comms-ink)">Every question — % answered right (times asked)</summary><div style="margin-top:6px">' + bars + '</div></details>' : '') + '</div>';
}

async function loadCompliance(weekStart) {
  _complianceWeek = weekStart;
  try {
    _compliance = await api('/api/admin/training/compliance?week_start=' + encodeURIComponent(weekStart));
  } catch (e) {
    _compliance = { week_start: weekStart, report: null, weeks: [], error: e.message };
  }
  var el = root(); if (!el || !_state) return;
  // Re-render just the panel and swap it in place. v135.2: found by its data-kind, NEVER by its heading text: with
  // Arabic on, the heading reads in Arabic, so the old "textContent starts with Compliance" test never matched and
  // the panel sat on "Loading..." for ever.
  var card = el.querySelector('.card[data-kind="compliance"]');
  if (card) {
    var tmp = document.createElement('div'); tmp.innerHTML = compliancePanel();
    card.replaceWith(tmp.firstChild);
  }
}

export function pickWeek(weekStart) { if (weekStart) loadCompliance(weekStart); }

export function runMonday() {
  if (!confirm('Run the Monday step now?\n\n1. Publish this week\'s session if it is approved.\n2. Draft next week\'s if there is none, and email the approvers.\n\nSafe to run twice — it will not re-publish or re-email.')) return;
  return run('Monday step ran', async function () {
    // (a fresh draft inside this step is the same ~1 minute write)
    var r = await api('/api/admin/training/run-monday', { method: 'POST', body: {} });
    if (r.error) toast(r.error, 'error');
  });
}

// v132.22 — Matt: "you dont know it is doing anything until it loads 20 or
// 30 seconds later. i worry others will spam". The instant a button is
// pressed every button in the tab is disabled and a banner with a live
// timer says what is happening; a second press does nothing. The Worker
// refuses a duplicate generation for the same week anyway (409).
var _busyTimer = null;
function showBusy(label, hint) {
  var el = root(); if (!el) return;
  el.querySelectorAll('button, select, input, textarea').forEach(function (b) { b.disabled = true; });
  var bar = document.getElementById('trainingBusy');
  if (!bar) { bar = document.createElement('div'); bar.id = 'trainingBusy'; el.insertBefore(bar, el.firstChild); }
  var started = Date.now();
  var paint = function () {
    var s = Math.round((Date.now() - started) / 1000);
    bar.innerHTML = '<div style="position:sticky;top:0;z-index:5;background:var(--adm-comms-orange-bg);border:1px solid var(--adm-comms-orange-line2);border-radius:10px;padding:10px 14px;margin-bottom:12px;font-size:13.5px;color:var(--adm-comms-orange-text);display:flex;gap:10px;align-items:center">' +
      '<span style="display:inline-block;width:14px;height:14px;border:2px solid #f97316;border-top-color:transparent;border-radius:50%;animation:trSpin 0.9s linear infinite"></span>' +
      '<span><b>' + esc(label) + '</b>' + (hint ? ' — ' + esc(hint) : '') + ' <span style="color:var(--adm-comms-orange-2)">(' + s + 's)</span></span></div>';
  };
  if (!document.getElementById('trainingBusyStyle')) { var st = document.createElement('style'); st.id = 'trainingBusyStyle'; st.textContent = '@keyframes trSpin{to{transform:rotate(360deg)}}'; document.head.appendChild(st); }
  paint();
  if (_busyTimer) clearInterval(_busyTimer);
  _busyTimer = setInterval(paint, 1000);
}
function hideBusy() {
  if (_busyTimer) { clearInterval(_busyTimer); _busyTimer = null; }
  var bar = document.getElementById('trainingBusy'); if (bar) bar.remove();
}

async function run(label, fn, hint) {
  if (_busy) { toast('Still working on the last one - one moment', 'error'); return; }
  _busy = true;
  showBusy(label.replace(/ed$/, 'ing').replace(/^Draft generating$/, 'Writing the draft'), hint || '');
  try { await fn(); toast(label + ' ✔'); }
  catch (e) { toast(label + ' failed: ' + e.message, 'error'); }
  finally { _busy = false; hideBusy(); await render(); }
}

export function generate() {
  var pick = document.getElementById('trainingPolicyPick');
  var detail = document.getElementById('trainingDetail');
  var body = { detail: detail ? detail.value.trim() : '' };
  if (pick && pick.value) body.policy_ids = [Number(pick.value)];
  // v132.24 — Head Office material: title + text + the week it is for
  var mat = document.getElementById('trainingMaterial'); var title = document.getElementById('trainingTopic'); var wk = document.getElementById('trainingWeekPick');
  var material = mat ? mat.value.trim() : '';
  // v133.86 (overnight sweep, L): "Your own topic" with an empty material box dropped the typed title and
  // made a PAID model call on a topic Shift chose. Own topic needs its material; nothing is sent without it.
  if (_topicMode === 'own' && !material) { toast('Paste the material for your own topic first (or choose a policy topic)', 'error'); return; }
  if (material) {
    if (!(title && title.value.trim())) { toast('Give the material session a title first', 'error'); return; }
    body.material_text = material; body.topic = title.value.trim();
    if (wk && wk.value) body.week_start = wk.value;
  }
  return run('Draft generated', async function () { await api('/api/admin/training/generate', { method: 'POST', body: body }); }, 'Shift is reading the policy and writing the session - about a minute. Please do not press again.');
}

export function approve(id) {
  if (!confirm('Approve this session? It will publish to every store on its Monday at 05:05 Dubai.')) return;
  return run('Approved', async function () { await api('/api/admin/training/' + id + '/approve', { method: 'POST', body: {} }); });
}

export function decline(id) {
  var reason = prompt('Why decline it? (optional — the writer sees this when you regenerate). If it was approved, it will NOT publish on Monday until you approve a replacement.') ;
  if (reason === null) return;
  return run('Declined', async function () { await api('/api/admin/training/' + id + '/decline', { method: 'POST', body: { reason: reason } }); });
}

export function sendApproval(id) {
  var who = (_state && _state.approvers) || [];
  if (!who.length) { toast('No approvers set — add them in Automated emails first', 'error'); return; }
  if (!confirm('Email ' + who.length + ' approver' + (who.length === 1 ? '' : 's') + ' now with their personal approve/decline links?')) return;
  return run('Approvers emailed', async function () {
    var r = await api('/api/admin/training/' + id + '/send-approval', { method: 'POST', body: {} });
    if (r.unknown && r.unknown.length) toast('Not sent to ' + r.unknown.join(', ') + ' — not a Shift account', 'error');
  });
}

export function preview(id) {
  return run('Preview sent to you', async function () { await api('/api/admin/training/' + id + '/preview', { method: 'POST', body: {} }); }, 'Sending the approval email to you only.');
}

window.renderTrainingAdmin = render;
